
export type ExtractionStatus = "uploaded" | "extracting" | "extracted" | "failed";

export type ExtractionConfig = {
  maxChars: number;
  maxOverlap: number;
  chunking_strategy: "text" | "markdown" | "yaml";
  imagesEnabled: boolean;
  ocrEnabled: boolean;
  ocrLanguage: string;
  detectChapters?: boolean;
};

export const DEFAULT_EXTRACTION_CONFIG: ExtractionConfig = {
  maxChars: 1000,
  maxOverlap: 200,
  chunking_strategy: "text",
  imagesEnabled: true,
  ocrEnabled: true,
  ocrLanguage: "eng",
  detectChapters: false,
};

export type DocSearchChunkDisplayConfig = {
  showFilename: boolean;
  showPageNumber: boolean;
  showScore: boolean;
  showChapter?: boolean;
};

export const DEFAULT_CHUNK_DISPLAY_CONFIG: DocSearchChunkDisplayConfig = {
  showFilename: false,
  showPageNumber: false,
  showScore: false,
  showChapter: false,
};

// Ein Eintrag im (automatisch erkannten) Inhaltsverzeichnis eines Dokuments.
export type DocumentTocEntry = {
  title: string;
  level: number;
  page?: number;
};

export const mimeTypesEmddingCompatible: { type: string, suffix: string }[] = [
  { type: "text/x-c", suffix: ".c" },
  { type: "text/x-csharp", suffix: ".cs" },
  { type: "text/x-c++", suffix: ".cpp" },
  { type: "text/x-java", suffix: ".java" },
  { type: "application/json", suffix: ".json" },
  { type: "text/markdown", suffix: ".md" },
  { type: "text/x-php", suffix: ".php" },
  { type: "text/x-python", suffix: ".py" },
  { type: "text/x-script.python", suffix: ".py" },
  { type: "text/x-ruby", suffix: ".rb" },
  { type: "text/x-tex", suffix: ".tex" },
  { type: "text/plain", suffix: ".txt" },
  { type: "text/css", suffix: ".css" },
  { type: "text/javascript", suffix: ".js" },
  { type: "application/x-sh", suffix: ".sh" },
  { type: "application/typescript", suffix: ".ts" },
]

export const mimeTypesNoEmbed: { type: string, suffix: string }[] = [
  { type: "image/png", suffix: ".png" },
  { type: "image/jpeg", suffix: ".jpeg" },
  { type: "image/jpg", suffix: ".jpg" },
  { type: "image/svg+xml", suffix: ".svg" },
  { type: "image/gif", suffix: ".gif" }
]

export const mimeTypesToConvert: { type: string, suffix: string }[] = [
  { type: "text/html", suffix: ".html" },
  { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", suffix: ".docx" },
  { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation", suffix: ".pptx" },
  { type: "application/pdf", suffix: ".pdf" },
]

export const allowedFileTypes = [...mimeTypesToConvert, ...mimeTypesEmddingCompatible]

export type BotDocFileMeta<ID = string> = {
  botId: ID,
  enabledForKnowledge: boolean,
  sourceFileId?: ID,
  skillId: string,
  description: string,
  name: string,
  chunkCount: number,
}

export type DocSourceType = "upload" | "website" | "sharepoint-file" | "sharepoint-page"

export type DocSource = {
  type: DocSourceType,
  id?: string,
  path?: string,
  url?: string,
  urls?: string[]
}

export type DocAutoUpdate<D = string> = {
  enabled: boolean,
  intervalDays?: number,
  disabledReason?: "manual" | "source_deleted",
  lastSyncAt?: D,
  lastSyncStatus?: "success" | "failed" | "source_deleted",
  lastSyncError?: string
}

export type DocSharepointState<D = string> = {
  sourceDeletedAt?: D,
  lastSourceModifiedAt?: D
}

export type DocWebsiteState<D = string> = {
  lastAutoUpdateAt?: D
}

export type SearchDoc<ID = string, D = string> = {
  _id: ID,
  chunkCount: number,
  botId: ID | null,
  projectId: ID,
  userId?: ID,
  projectName: string,
  docSearchId: string,
  uploadDate: D,
  skillId: string,
  filename: string,
  enabledForKnowledge: boolean,
  publiclyReachable: boolean,
  functionName: string,
  description: string,
  uploadedBy: string,
  mimetype: string,
  source?: DocSource,
  sharepoint?: DocSharepointState<D>,
  website?: DocWebsiteState<D>,
  autoUpdate?: DocAutoUpdate<D>,
  extractionStatus?: ExtractionStatus,
  extractionConfig?: ExtractionConfig,
  extractionError?: string,
  extractionErrorType?: string,
  chunkDisplayConfig?: DocSearchChunkDisplayConfig,
  searchType?: "vector" | "text" | "combined",
  tableOfContents?: DocumentTocEntry[],
  // Merkt sich Ergebnisse vergangener Suchen ueber dieses einzelne Dokument (als Tool) ueber
  // mehrere Chat-Runs hinweg. Unabhaengig von activeDocSearchMemory* auf der Bot-Config.
  docSearchMemoryEnabled?: boolean,
  docSearchMemoryLimit?: number,
}

export type SpfBucketFileFrontend<ID = string, META = BotDocFileMeta<ID>> = {
  _id: string,
  length: number,
  uploadDate: Date
  filename: string,
  contentType: string,
  metadata: META,
}